// src/views/Admin/Companies/components/CreateLocalModal/CreateLocalModal.tsx

import React, { useState, useEffect } from 'react';
import { HiOutlineBuildingStorefront, HiOutlineXMark } from 'react-icons/hi2';
import './CreateLocalModal.scss';

export interface LocalFormData {
  local_name: string;
  address: string;
  company_id: number;
  city_id: number;
  state: boolean;
  code?: string;
}

interface CityOption {
  city_id: number;
  city_name: string;
}

interface CreateLocalModalProps {
  isOpen: boolean;
  companyId: number;
  companyName: string;
  companyAddress: string;
  companyCityId: number;
  cities: CityOption[];
  onClose: () => void;
  onSubmit: (data: LocalFormData) => Promise<void>;
}

const CreateLocalModal: React.FC<CreateLocalModalProps> = ({
  isOpen,
  companyId,
  companyName,
  companyAddress,
  companyCityId,
  cities,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<LocalFormData>({
    local_name: '',
    address: '',
    company_id: 0,
    city_id: 1,
    state: true,
    code: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill form data when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        local_name: '',
        address: companyAddress || '',
        company_id: companyId,
        city_id: companyCityId || 1,
        state: true,
        code: ''
      });
      setError(null);
    }
  }, [isOpen, companyId, companyAddress, companyCityId]);

  const handleChange = (field: keyof LocalFormData, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.local_name.trim()) {
      setError('El nombre del local es requerido');
      return;
    }
    if (!formData.address.trim()) {
      setError('La dirección es requerida');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(formData);
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.response?.data || 'Error al crear el local';
      setError(typeof message === 'string' ? message : 'Error al crear el local');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
    if (e.key === 'Enter' && !isSubmitting) {
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-local-modal-overlay" onClick={handleClose}>
      <div 
        className="create-local-modal" 
        onClick={(e) => e.stopPropagation()} 
        onKeyDown={handleKeyDown}
      >
        <button className="close-button" onClick={handleClose} disabled={isSubmitting}>
          <HiOutlineXMark size={20} />
        </button>

        <div className="modal-icon">
          <HiOutlineBuildingStorefront size={28} />
        </div>

        <h2>Nuevo Local</h2>
        <p className="modal-subtitle">
          Crear local para <strong>{companyName}</strong>
        </p>

        {error && <div className="error-message">{error}</div>}

        <div className="form-grid">
          <div className="form-group full">
            <label>Nombre del Local *</label>
            <input
              type="text"
              value={formData.local_name}
              onChange={(e) => handleChange('local_name', e.target.value)}
              placeholder="Ej: Sucursal Centro"
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div className="form-group full">
            <label>Dirección *</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="Av. Principal 123, Santiago"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Ciudad</label>
            <select
              value={formData.city_id}
              onChange={(e) => handleChange('city_id', parseInt(e.target.value) || 1)}
              disabled={isSubmitting}
            >
              {cities.map(city => (
                <option key={city.city_id} value={city.city_id}>
                  {city.city_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Código (Opcional)</label>
            <input
              type="text"
              value={formData.code || ''}
              onChange={(e) => handleChange('code', e.target.value)}
              placeholder="LOC001"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group full toggle-group">
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={formData.state}
                onChange={(e) => handleChange('state', e.target.checked)}
                disabled={isSubmitting}
              />
              <span>Local Activo</span>
            </label>
          </div>
        </div>

        <div className="modal-actions">
          <button 
            className="cancel-button" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button 
            className="submit-button"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner" />
                Creando...
              </>
            ) : (
              <>
                <HiOutlineBuildingStorefront size={18} />
                Crear Local
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateLocalModal;
